import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { selectItemRowClassName } from '@/components/ui/select'
import { cn } from '@/shared/lib/utils'

export type CombustionFuelOption = {
  id: string
  name: string
}

type InstallationCombustionFuelSelectProps = {
  value: string
  onValueChange: (fuelTypeId: string) => void
  options: CombustionFuelOption[]
  /** When false, clears the search field (e.g. parent dialog closed). */
  active?: boolean
  disabled?: boolean
  listAriaLabel: string
}

function matchesFuelQuery(option: CombustionFuelOption, query: string): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) {
    return true
  }
  return (
    option.name.toLowerCase().includes(trimmed) ||
    option.id.toLowerCase().includes(trimmed)
  )
}

/**
 * Searchable list for picking a combustion fuel type. Intended inside a parent dialog.
 */
export function InstallationCombustionFuelSelect({
  value,
  onValueChange,
  options,
  active = true,
  disabled,
  listAriaLabel,
}: InstallationCombustionFuelSelectProps) {
  const { t } = useTranslation('installations')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (!active) {
      setSearchQuery('')
    }
  }, [active])

  const filteredOptions = useMemo(
    () => options.filter((fuelType) => matchesFuelQuery(fuelType, searchQuery)),
    [options, searchQuery]
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-border px-4 py-3">
        <Input
          autoFocus
          value={searchQuery}
          disabled={disabled}
          placeholder={t('configuration.fuels.searchPlaceholder')}
          onChange={(event) => {
            setSearchQuery(event.target.value)
          }}
        />
      </div>
      <div
        role="listbox"
        className="min-h-0 flex-1 overflow-y-auto p-1"
        aria-label={listAriaLabel}
      >
        {filteredOptions.length === 0 ? (
          <p className="px-2 py-3 text-center text-sm text-muted-foreground">
            {t('configuration.fuels.noSearchResults')}
          </p>
        ) : (
          filteredOptions.map((fuelType) => {
            const isSelected = fuelType.id === value
            return (
              <button
                key={fuelType.id}
                type="button"
                role="option"
                disabled={disabled}
                aria-selected={isSelected}
                className={cn(
                  selectItemRowClassName,
                  isSelected && 'bg-accent text-accent-foreground'
                )}
                onClick={() => {
                  onValueChange(fuelType.id)
                }}
              >
                <span className="truncate">{fuelType.name}</span>
                <span className="absolute inset-e-2 flex size-3.5 items-center justify-center">
                  {isSelected ? <CheckIcon className="size-4" aria-hidden /> : null}
                </span>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
